import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { HiCheck, HiClock, HiXMark, HiArrowLeft } from 'react-icons/hi2';
import type { DemandaAutorizacao } from '../views/demand-view/hooks/queries/useAutorizacoesQuery';

type FixedRoleType =
  | 'gestor_demandante'
  | 'executivo_demandante'
  | 'executivo_demandado'
  | 'gestor_demandado'

export interface FixedRolePreviewConfig {
  gestorDemandante?: boolean;
  executivoDemandante?: boolean;
  executivoDemandado?: boolean;
  gestorDemandado?: boolean;
}

type TimelineAutorizacao = DemandaAutorizacao & {
  __fixedRoleType?: FixedRoleType;
};

const EMPTY_AUTORIZACOES: DemandaAutorizacao[] = [];

const FIXED_ROLE_IDS: Record<FixedRoleType, number> = {
  gestor_demandante: -1,
  executivo_demandante: -2,
  executivo_demandado: -3,
  gestor_demandado: -4,
};

const FIXED_ROLE_META: Record<FixedRoleType, { label: string; initials: string; previewBg: string }> = {
  gestor_demandante: {
    label: 'Gestor da Unidade Demandante',
    initials: 'GD',
    previewBg:
      'bg-gradient-to-br from-blue-500 to-blue-600 dark:from-blue-600 dark:to-blue-700 border-blue-300 dark:border-blue-600',
  },
  executivo_demandante: {
    label: 'Executivo da Unidade Demandante',
    initials: 'ED',
    previewBg:
      'bg-gradient-to-br from-indigo-500 to-indigo-600 dark:from-indigo-600 dark:to-indigo-700 border-indigo-300 dark:border-indigo-600',
  },
  executivo_demandado: {
    label: 'Executivo da Unidade Demandada',
    initials: 'ED',
    previewBg:
      'bg-gradient-to-br from-teal-500 to-teal-600 dark:from-teal-600 dark:to-teal-700 border-teal-300 dark:border-teal-600',
  },
  gestor_demandado: {
    label: 'Gestor da Unidade Demandada',
    initials: 'GD',
    previewBg:
      'bg-gradient-to-br from-emerald-500 to-emerald-600 dark:from-emerald-600 dark:to-emerald-700 border-emerald-300 dark:border-emerald-600',
  },
};

const hasAnyFixedRole = (config?: FixedRolePreviewConfig) => {
  if (!config) return false;
  return (
    !!config.gestorDemandante ||
    !!config.executivoDemandante ||
    !!config.executivoDemandado ||
    !!config.gestorDemandado
  );
};

const createFixedRoleAuthorization = (
  role: FixedRoleType,
  timestamp: string
): TimelineAutorizacao => ({
  id: FIXED_ROLE_IDS[role],
  demanda_fk: 0,
  autorizador_fk: FIXED_ROLE_IDS[role],
  ordem: 0,
  status: 'AGUARDANDO',
  created_at: timestamp,
  updated_at: timestamp,
  autorizador: {
    id: FIXED_ROLE_IDS[role],
    nome_setor: FIXED_ROLE_META[role].label,
    equipe: false,
  },
  __fixedRoleType: role,
});

interface AutorizacaoTimelineProps {
  autorizacoes: DemandaAutorizacao[];
  isSequencial: boolean;
  className?: string;
  previewMode?: boolean; // Novo: modo preview para criação de demandas
  fixedRolePreview?: FixedRolePreviewConfig;
}

// Componente de Tooltip usando Portal
interface TooltipPortalProps {
  isVisible: boolean;
  targetRef: React.RefObject<HTMLDivElement | null>;
  nomeSetor: string;
  status?: string;
  usuarioAprovador?: { id: number; name: string };
  previewMode?: boolean; // Novo: para ajustar conteúdo do tooltip
}

const TooltipPortal: React.FC<TooltipPortalProps> = ({
  isVisible,
  targetRef,
  nomeSetor,
  status,
  usuarioAprovador,
  previewMode = false,
}) => {
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (isVisible && targetRef.current) {
      const rect = targetRef.current.getBoundingClientRect();
      setPosition({
        top: rect.top - 8, // 8px acima do elemento
        left: rect.left + rect.width / 2, // Centralizado
      });
    }
  }, [isVisible, targetRef]);

  if (!isVisible) return null;

  return createPortal(
    <div
      className="fixed pointer-events-none z-50"
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
        transform: 'translate(-50%, -100%)',
      }}
    >
      <div className="px-2.5 py-1.5 bg-gray-800/95 dark:bg-gray-900/95 backdrop-blur-sm text-white text-[11px] rounded-md shadow-xl whitespace-nowrap animate-in fade-in-0 zoom-in-95 duration-200">
        <div className="font-semibold text-white">{nomeSetor || 'Setor Desconhecido'}</div>
        {/* No modo preview, não mostra status nem aprovador */}
        {!previewMode && status && (
          <>
            <div className="text-gray-300 text-[10px] mt-0.5">
              {(status === 'APROVADO' || status === 'APROVADA') && '✓ Aprovado'}
              {(status === 'REPROVADO' || status === 'REPROVADA') && '✗ Reprovado'}
              {status === 'AGUARDANDO' && '⏳ Aguardando'}
            </div>
            {status !== 'AGUARDANDO' && usuarioAprovador && (
              <div className="text-gray-400 text-[9px] mt-0.5">por {usuarioAprovador.name}</div>
            )}
          </>
        )}
        {/* Seta do tooltip */}
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-[3px] border-transparent border-t-gray-800/95 dark:border-t-gray-900/95"></div>
      </div>
    </div>,
    document.body
  );
};

export const AutorizacaoTimeline: React.FC<AutorizacaoTimelineProps> = ({
  autorizacoes,
  isSequencial,
  className = '',
  previewMode = false,
  fixedRolePreview,
}) => {
  const safeAutorizacoes = useMemo(
    () => (Array.isArray(autorizacoes) ? autorizacoes : EMPTY_AUTORIZACOES),
    [autorizacoes]
  );
  const hasFixedRolePreview = hasAnyFixedRole(fixedRolePreview);

  const sequencialLayout = isSequencial || hasFixedRolePreview;

  // Ordenar autorizações: sequencial por ordem, paralelo por data de criação
  const sortedAutorizacoes = useMemo(() => {
    const base = [...safeAutorizacoes];
    return base.sort((a, b) => {
      // Try to use explicit ordem (root) then fallback to pivot.ordem from autorizador
      const ordemA = a.ordem ?? a.autorizador?.pivot?.ordem ?? null;
      const ordemB = b.ordem ?? b.autorizador?.pivot?.ordem ?? null;

      if (sequencialLayout && ordemA !== null && ordemB !== null) {
        return Number(ordemA) - Number(ordemB);
      }

      return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
    });
  }, [safeAutorizacoes, sequencialLayout]);

  const timelineAutorizacoes = useMemo(() => {
    if (!hasFixedRolePreview) {
      return sortedAutorizacoes as TimelineAutorizacao[];
    }

    const timestamp = new Date().toISOString();
    const beforeFixed: TimelineAutorizacao[] = [];
    const afterFixed: TimelineAutorizacao[] = [];

    if (fixedRolePreview?.gestorDemandante) {
      beforeFixed.push(createFixedRoleAuthorization('gestor_demandante', timestamp));
    }
    if (fixedRolePreview?.executivoDemandante) {
      beforeFixed.push(createFixedRoleAuthorization('executivo_demandante', timestamp));
    }

    if (fixedRolePreview?.executivoDemandado) {
      afterFixed.push(createFixedRoleAuthorization('executivo_demandado', timestamp));
    }
    if (fixedRolePreview?.gestorDemandado) {
      afterFixed.push(createFixedRoleAuthorization('gestor_demandado', timestamp));
    }

    const middle = sortedAutorizacoes.map(aut => ({ ...aut })) as TimelineAutorizacao[];
    const merged = [...beforeFixed, ...middle, ...afterFixed];

    return merged.map((aut, idx) => ({
      ...aut,
      ordem: sequencialLayout ? idx + 1 : aut.ordem,
    }));
  }, [sortedAutorizacoes, fixedRolePreview, hasFixedRolePreview, sequencialLayout]);

  if (timelineAutorizacoes.length === 0) {
    return null;
  }

  const renderedAutorizacoes = sequencialLayout
    ? [...timelineAutorizacoes].reverse()
    : timelineAutorizacoes;

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'APROVADO':
      case 'APROVADA':
        return <HiCheck className="w-2.5 h-2.5 text-white" />;
      case 'REPROVADO':
      case 'REPROVADA':
        return <HiXMark className="w-2.5 h-2.5 text-white" />;
      case 'AGUARDANDO':
      default:
        return <HiClock className="w-2.5 h-2.5 text-white" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'APROVADO':
      case 'APROVADA':
        return 'bg-gradient-to-br from-emerald-400 to-emerald-500 dark:from-emerald-500 dark:to-emerald-600 border-emerald-300 dark:border-emerald-600';
      case 'REPROVADO':
      case 'REPROVADA':
        return 'bg-gradient-to-br from-rose-400 to-rose-500 dark:from-rose-500 dark:to-rose-600 border-rose-300 dark:border-rose-600';
      case 'AGUARDANDO':
      default:
        return 'bg-gradient-to-br from-amber-400 to-amber-500 dark:from-amber-500 dark:to-amber-600 border-amber-300 dark:border-amber-600';
    }
  };

  const getInitials = (nomeSetor: string) => {
    return (
      nomeSetor
        .split(/[_\s]+/)
        .filter(Boolean)
        .map(word => word[0])
        .join('')
        .toUpperCase()
        .slice(0, 2) || '??'
    );
  };

  const resolveInitials = (autorizacao: TimelineAutorizacao) => {
    if (autorizacao.__fixedRoleType) {
      return FIXED_ROLE_META[autorizacao.__fixedRoleType].initials;
    }
    return getInitials(autorizacao.autorizador?.nome_setor || 'XX');
  };

  const getPreviewBubbleColor = (autorizacao: TimelineAutorizacao) => {
    if (!previewMode) {
      return getStatusColor(autorizacao.status);
    }
    if (autorizacao.__fixedRoleType) {
      return FIXED_ROLE_META[autorizacao.__fixedRoleType].previewBg;
    }
    return 'bg-gradient-to-br from-gray-400 to-gray-500 dark:from-gray-500 dark:to-gray-600 border-gray-300 dark:border-gray-600';
  };

  return (
    <div className={`${className}`}>
      {/* Timeline Horizontal - Direita para Esquerda se sequencial */}
      <div className={`flex items-center gap-2 ${sequencialLayout ? 'flex-row-reverse justify-end' : 'flex-row justify-start'}`}>
        {/* Reverter o array quando sequencial para que 1 -> 2 -> 3 apareça da direita para esquerda */}
        {renderedAutorizacoes.map((autorizacao, index) => {
          // eslint-disable-next-line react-hooks/rules-of-hooks
          const [isHovered, setIsHovered] = useState(false);
          // eslint-disable-next-line react-hooks/rules-of-hooks
          const iconRef = useRef<HTMLDivElement>(null);

          return (
            <React.Fragment key={autorizacao.id}>
            {/* Ícone do Setor */}
            <div 
              className="group relative"
              ref={iconRef}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              <div
                className={`
                  relative h-8 w-8 rounded-full flex items-center justify-center
                  ${getPreviewBubbleColor(autorizacao)}
                  border shadow-sm
                  transition-all duration-200 hover:scale-110 hover:shadow-md cursor-pointer
                `}
                title={autorizacao.autorizador?.nome_setor || 'Setor Desconhecido'}
              >
                {/* Iniciais do setor */}
                <span className="text-[10px] font-bold text-white drop-shadow-sm">
                  {resolveInitials(autorizacao)}
                </span>
                
                {/* Badge de status no canto inferior direito - não mostrar no preview mode */}
                {!previewMode && (
                  <div className={`absolute -bottom-0.5 -right-0.5 rounded-full p-0.5 w-4 h-4 flex items-center justify-center ${getStatusColor(autorizacao.status)} shadow-sm`}>
                    {getStatusIcon(autorizacao.status)}
                  </div>
                )}

                {/* Número da ordem (se sequencial) - canto superior esquerdo */}
                {sequencialLayout && (() => {
                  const ordemValor = autorizacao.ordem ?? autorizacao.autorizador?.pivot?.ordem
                  return (
                    <div className="absolute -top-0.5 -left-0.5 bg-gradient-to-br from-blue-500 to-blue-600 dark:from-blue-600 dark:to-blue-700 text-white rounded-full w-4 h-4 flex items-center justify-center text-[9px] font-bold shadow-sm border border-blue-400 dark:border-blue-500">
                      {ordemValor !== null && ordemValor !== undefined ? ordemValor : index + 1}
                    </div>
                  )
                })()}
              </div>

              {/* Tooltip usando Portal */}
              <TooltipPortal
                isVisible={isHovered}
                targetRef={iconRef}
                nomeSetor={autorizacao.autorizador?.nome_setor || 'Setor Desconhecido'}
                status={autorizacao.status}
                previewMode={previewMode}
                {...(autorizacao.usuario_aprovador && { usuarioAprovador: autorizacao.usuario_aprovador })}
              />
            </div>

            {/* Seta entre ícones - mais discreta */}
            {sequencialLayout && index < renderedAutorizacoes.length - 1 && (
              <HiArrowLeft className="w-3 h-3 text-gray-300 dark:text-gray-600 flex-shrink-0 rotate-180 opacity-60" />
            )}
          </React.Fragment>
        )})}
      </div>
    </div>
  );
};